import React, { useState, useEffect } from 'react';
import { X, GitBranch, AlertTriangle, ExternalLink } from 'lucide-react';
import { itineraryVersionService } from '../../services/itineraryVersionService';

interface VersionInfo {
  quotation_id: number;
  quotation_number: string;
  itinerary_id: number;
  version_number: number;
  itinerary_name: string;
  lead_guest_name: string;
  travel_date: string;
  given_price: number;
  itinerary_currency: string;
  itinerary_status: string;
  invoice_amount: number | null;
  total_paid: number;
  booked_items_count: number;
}

interface VersionsModalProps {
  itineraryId: number;
  onClose: () => void;
  onNavigate?: (itineraryId: number) => void;
  selectedItineraryId?: number;
  onCreateVersion?: (itineraryId: number) => void; // called when Create Version is clicked
}

const VersionsModal: React.FC<VersionsModalProps> = ({ itineraryId, onClose, onNavigate, selectedItineraryId, onCreateVersion }) => {
  const [versions, setVersions] = useState<VersionInfo[]>([]);
  const [quotationNumber, setQuotationNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [noQuotation, setNoQuotation] = useState(false);

  useEffect(() => {
    fetchVersions();
  }, [itineraryId]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const res = await itineraryVersionService.getQuotationForItinerary(itineraryId);
      if (res.data.has_quotation === false) {
        setNoQuotation(true);
        return;
      }
      setQuotationNumber(res.data.quotation_number);
      setVersions(res.data.versions || []);
    } catch (err) {
      console.error('Error fetching versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      CONVERTED: 'bg-green-100 text-green-800',
      ARCHIVED: 'bg-gray-200 text-gray-600',
      PENDING_CONFIRMATION: 'bg-yellow-100 text-yellow-800',
      TENTATIVE_BOOKING: 'bg-blue-100 text-blue-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {loading ? 'Loading...' : noQuotation ? 'No Version History' : `${quotationNumber} — Version History`}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {!loading && noQuotation && (
            <div className="text-center py-8 text-gray-500">
              <GitBranch className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>This itinerary has no version history yet.</p>
              <p className="text-sm mt-1">Use "Create Version" to start versioning.</p>
            </div>
          )}

          {!loading && !noQuotation && versions.length > 0 && (
            <div className="space-y-3">
              {versions.map((v) => (
                <div
                  key={v.itinerary_id}
                  className={`border rounded-lg p-4 ${
                    v.itinerary_id === (selectedItineraryId || itineraryId)
                      ? 'border-indigo-400 bg-indigo-50'
                      : v.itinerary_id === itineraryId
                      ? 'border-blue-300 bg-blue-50'
                      : v.itinerary_status === 'ARCHIVED'
                      ? 'border-gray-200 bg-gray-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono font-bold text-gray-500">v{v.version_number}</span>
                      <span className="text-sm font-semibold text-gray-900">WH_{v.itinerary_id}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(v.itinerary_status)}`}>
                        {v.itinerary_status}
                      </span>
                      {v.itinerary_id === itineraryId && (
                        <span className="text-xs text-blue-600 font-medium">(current)</span>
                      )}
                      {selectedItineraryId && v.itinerary_id === selectedItineraryId && (
                        <span className="text-xs text-indigo-600 font-bold">← rework base</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {(v.itinerary_status !== 'ARCHIVED' && v.itinerary_status !== 'CONVERTED') && (
                        <button
                          onClick={() => {
                            onClose();
                            if (onCreateVersion) {
                              onCreateVersion(v.itinerary_id);
                            } else {
                              window.location.href = `/balify?version_of=${v.itinerary_id}`;
                            }
                          }}
                          className="flex items-center gap-1 px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700"
                        >
                          <GitBranch className="w-3 h-3" />
                          Create Version
                        </button>
                      )}
                      {onNavigate && v.itinerary_id !== itineraryId && (
                        <button
                          onClick={() => { onNavigate(v.itinerary_id); onClose(); }}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <ExternalLink className="w-3 h-3" /> Open
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-600">
                    <div>
                      <span className="text-gray-400">Name:</span>
                      <div className="font-medium text-gray-800 truncate">{v.itinerary_name || '—'}</div>
                    </div>
                    <div>
                      <span className="text-gray-400">Guest:</span>
                      <div className="font-medium text-gray-800 truncate">{v.lead_guest_name || '—'}</div>
                    </div>
                    <div>
                      <span className="text-gray-400">Price:</span>
                      <div className="font-medium text-gray-800">
                        {v.itinerary_currency} {v.given_price?.toLocaleString() || '0'}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-400">Travel:</span>
                      <div className="font-medium text-gray-800">
                        {v.travel_date ? new Date(v.travel_date).toLocaleDateString('en-GB') : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Financial info for converted/archived */}
                  {(v.itinerary_status === 'CONVERTED' || v.itinerary_status === 'ARCHIVED') && (
                    <div className="mt-2 pt-2 border-t border-gray-200 flex gap-4 text-xs">
                      {v.invoice_amount != null && (
                        <span className="text-gray-600">
                          Invoice: <span className="font-medium">{v.itinerary_currency} {v.invoice_amount.toLocaleString()}</span>
                        </span>
                      )}
                      {v.total_paid > 0 && (
                        <span className="text-green-600">
                          Paid: <span className="font-medium">{v.itinerary_currency} {v.total_paid.toLocaleString()}</span>
                        </span>
                      )}
                      {v.booked_items_count > 0 && (
                        <span className="text-blue-600">
                          {v.booked_items_count} booked items
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionsModal;
